import { useQuery } from '@tanstack/react-query';
import { WrappedNativeTokens } from '@shared/types/quoting-engine/wrappedNativeTokens';
import { isNativeTokenAddress } from '@/lib/asset/nativeAddressHelper';
export const convertBasicTokenInfoToCovalentBalanceItem = basicTokenInfo => {
  var _a, _b;
  if (!basicTokenInfo.token) {
    return {
      contract_address: '',
      contract_decimals: 0,
      contract_name: '',
      contract_ticker_symbol: '',
      logo_url: '',
      network: 0
    };
  }
  const token = basicTokenInfo.token;
  const tokenInfo = (_a = basicTokenInfo.tokenInfo) === null || _a === void 0 ? void 0 : _a[0];
  return {
    contract_address: token.address,
    contract_decimals: token.decimals,
    contract_name: token.name,
    contract_ticker_symbol: token.symbol,
    logo_url: (_b = token.info) === null || _b === void 0 ? void 0 : _b.imageThumbUrl,
    network: token.networkId,
    balance: '0',
    quote_rate: Number(tokenInfo === null || tokenInfo === void 0 ? void 0 : tokenInfo.priceUSD) || 0
  };
};
export const convertBasicToCovalentWithNatives = (tokenData, assetData) => {
  var _a;
  const _params = {
    token: tokenData === null || tokenData === void 0 ? void 0 : tokenData.token,
    tokenInfo: (_a = tokenData === null || tokenData === void 0 ? void 0 : tokenData.tokenInfo) === null || _a === void 0 ? void 0 : _a.results,
    prices: [tokenData === null || tokenData === void 0 ? void 0 : tokenData.prices]
  };
  const _convertedAsset = convertBasicTokenInfoToCovalentBalanceItem(_params);
  const asset = assetData === null || assetData === void 0 ? void 0 : assetData.balances.find(token => {
    var _a;
    return token.contract_address === _convertedAsset.contract_address && token.network === _convertedAsset.network || isNativeTokenAddress(token.contract_address) && ((_a = WrappedNativeTokens.get(token.network)) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === _convertedAsset.contract_address;
  });
  if (asset) {
    _convertedAsset.contract_address = asset === null || asset === void 0 ? void 0 : asset.contract_address;
    _convertedAsset.contract_display_name = asset === null || asset === void 0 ? void 0 : asset.contract_display_name;
    _convertedAsset.contract_name = asset === null || asset === void 0 ? void 0 : asset.contract_name;
    _convertedAsset.usd_value = asset === null || asset === void 0 ? void 0 : asset.usd_value;
    _convertedAsset.balance = asset === null || asset === void 0 ? void 0 : asset.balance;
    _convertedAsset.quote_rate = asset === null || asset === void 0 ? void 0 : asset.quote_rate;
    _convertedAsset.quote = asset === null || asset === void 0 ? void 0 : asset.quote;
    _convertedAsset.formatted_balance = asset === null || asset === void 0 ? void 0 : asset.formatted_balance;
  }
  return _convertedAsset;
};
export const getBasicTokenInfo = async (network, token, includePairInfo = false) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
  let tokenData = {
    token: null,
    tokenInfo: null,
    prices: null
  };
  const baseUrl = process.env.NEXTAUTH_URL || '';
  const url = `${baseUrl}/api/explore/basicToken?token=${token}&network=${network}&includePairInfo=${includePairInfo}`;
  const response = await fetch(url, {
    headers: {
      'Cache-Control': 'no-store, no-cache, max-age=0, must-revalidate',
      Pragma: 'no-cache'
    }
  });
  const payload = await response.json();
  const data = payload === null || payload === void 0 ? void 0 : payload.data;
  tokenData = {
    pairs: (_b = (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.listPairsWithMetadataForToken) !== null && _b !== void 0 ? _b : null,
    token: (_d = (_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.token) !== null && _d !== void 0 ? _d : null,
    tokenInfo: (_f = (_e = data === null || data === void 0 ? void 0 : data.data) === null || _e === void 0 ? void 0 : _e.filterTokens) !== null && _f !== void 0 ? _f : null,
    prices: (_h = (_g = data === null || data === void 0 ? void 0 : data.data) === null || _g === void 0 ? void 0 : _g.getTokenPrices) !== null && _h !== void 0 ? _h : null,
    tokenStats: (_k = (_j = data === null || data === void 0 ? void 0 : data.data) === null || _j === void 0 ? void 0 : _j.aggregatedPairInfo) !== null && _k !== void 0 ? _k : null
  };
  return tokenData;
};
export default function useBasicTokenInfo({
  network,
  token
}) {
  const query = useQuery({
    queryKey: ['basicTokenInfo', network, token],
    queryFn: async () => {
      const data = await getBasicTokenInfo(network, token);
      return data;
    },
    enabled: !!network && !!token
  });
  return query;
}
export function useBasicTokenInfoList(tokenPairs, {
  enabled = true
} = {}) {
  const query = useQuery({
    queryKey: ['savedTokensQuery', tokenPairs],
    queryFn: async () => {
      // Fetch data for all token pairs in parallel
      const results = await Promise.all(tokenPairs.map(async token => {
        var _a, _b, _c, _d;
        if (!token.network || !token.token) return null;
        let data = await getBasicTokenInfo(token.network, token.token);
        data = Object.assign(Object.assign(Object.assign({}, data), (_b = (_a = data === null || data === void 0 ? void 0 : data.tokenInfo) === null || _a === void 0 ? void 0 : _a.results) === null || _b === void 0 ? void 0 : _b[0]), {
          token: Object.assign(Object.assign({}, data === null || data === void 0 ? void 0 : data.token), {
            imageLargeUrl: (_d = (_c = data === null || data === void 0 ? void 0 : data.token) === null || _c === void 0 ? void 0 : _c.info) === null || _d === void 0 ? void 0 : _d.imageThumbUrl
          })
        });
        return data;
      }));
      // Filter out any null or undefined results
      return results;
    },
    enabled: tokenPairs.length > 0 && enabled
  });
  return query;
}